import React, { useEffect, useMemo } from 'react';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';

import JBImage from 'components/essentials/JBImage';
import MobileModal from 'components/consumer/MobileModal';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomerQuote from 'components/consumer/CustomerQuote';
import IconSimpleArrowLeft from 'components/icons/iconSimpleArrowLeft';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import getShortCustomerName from 'commons/getShortCustomerName';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useScreen from 'hooks/useScreen';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import ProductCardStatic from '../ProductCardStatic';

const GridWithSidebarPhotoModal = ({
  toggleModal,
  photosList,
  loadingMore,
  loadMore,
  photo,
}) => {
  const { isMobile, isDesktop, windowWidth } = useScreen();
  const infiniteScrollThreshold = windowWidth <= 768 ? '4800px' : '6200px';

  // product IDs are extracted from photoList and duplciates are removed by passing into a Set with a spread operator
  const productIds = useMemo(() => {
    if (!!photosList && photosList.length) {
      return uniq(
        photosList.flatMap(listItem =>
          listItem.products.map(product =>
            Number.parseInt(product.product_id, 10)
          )
        )
      );
    }
    return [];
  }, [photosList]);

  const { products: staticProductData } = useStaticProductCardData({
    productIds,
  });

  useEffect(() => {
    setTimeout(() => {
      const photoId = !!photo && photo.photo_id;

      if (photoId) {
        const photoContainer = document.getElementById(
          `grid-with-sidebar-photo-modal__image-container-${photoId}`
        );

        if (photoContainer) {
          photoContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }
    }, 200);
  }, [photo]);

  const content = (
    <div className="fixed left-0 w-full h-screen bg-white z-[4002] text-gray lg:top-[var(--visible-promotion-banner-height)]">
      {!isMobile && (
        <div className="fixed w-full p-0 bg-white flex items-stretch justify-between z-[1006] flex-wrap md:px-[3.333vw] md:py-0 md:h-[68px] md:[border-bottom:1px_solid_#e5e5e5]">
          <div className="flex content-start items-stretch flex-[1_1] px-[3.333vw] py-4 md:p-0 bg-white">
            <button
              type="button"
              onClick={toggleModal}
              className="flex items-center p-[5px] cursor-pointer"
            >
              <IconSimpleArrowLeft className="mr-2 flex-[0_1_12px] leading-[130%] transform-[scale(.7)] md:scale-100" />
            </button>
          </div>
        </div>
      )}
      <div className="absolute top-0 left-0 w-full h-full pt-6 px-[3.333vw] pb-[9.2rem] mx-0 my-[50px] overflow-auto scroll-smooth max-[1023px]:pr-0 max-[1023px]:pl-0 max-[1023px]:pt-0 max-[1023px]:mt-0">
        <InfiniteScroll
          dataLength={photosList && photosList.length}
          next={loadMore}
          hasMore={!!loadMore}
          scrollThreshold={infiniteScrollThreshold}
        >
          {photosList.map(item => (
            <div
              className="overflow-hidden mb-8 relative max-[767px]:flex max-[767px]:flex-col-reverse lg:flex lg:flex-wrap"
              id={`grid-with-sidebar-photo-modal__image-container-${item.photo_id}`}
            >
              <div className="relative flex justify-center flex-col mb-4 max-[1023px]:pr-[3.333vw] max-[1023px]:pl-[3.333vw] lg:w-1/2 lg:pr-10">
                <div className="text-base font-bold md:text-lg md:font-bold lg:text-xl lg:block lg:mb-0 lg:font-bold">
                  <CustomerNameWithEmoji className="text-2xl font-bold items-center text-gray flex mb-3">
                    {getShortCustomerName(item.photo_full_name)}
                  </CustomerNameWithEmoji>
                </div>
                {item.photo_comment && (
                  <div className="text-base md:text-lg">
                    <CustomerQuote showQuoteMark>
                      {item.photo_comment}
                    </CustomerQuote>
                  </div>
                )}
                {!!item.products && !!item.products.length && (
                  <div className="flex flex-wrap pb-[50px] pt-8 max-[1023px]:grid max-[1023px]:gap-[10px] max-[1023px]:grid-flow-col max-[1023px]:grid-rows-[minmax(100px,1fr)] max-[1023px]:overflow-x-scroll max-[1023px]:overflow-y-hidden max-[1023px]:pb-0 lg:[border-top:1px_solid_#dbdbdb] lg:mt-2 lg:pt-6">
                    {!!staticProductData.length &&
                      item.products
                        .map(({ product_id }) => {
                          const product = staticProductData.find(
                            productItem =>
                              `${productItem?.id}` === `${product_id}`
                          );

                          if (!product) {
                            return null;
                          }

                          return (
                            <div className="w-60">
                              <ProductCardStatic
                                {...product}
                                className="mr-4 mb-4"
                                key={product_id}
                                layout="minimal"
                              />
                            </div>
                          );
                        })
                        .filter(Boolean)}
                  </div>
                )}
              </div>
              <div className="mt-4 lg:w-1/2 lg:mt-0 lg:pl-2 first-of-type:max-[1023px]:mt-0">
                <div className="h-auto [&>div]:w-full [&>div>div]:w-full [&_img]:min-h-full [&_img]:object-cover [&_img]:object-center max-[767px]:relative max-[767px]:mb-4 max-[767px]:text-center">
                  <JBImage
                    sizes={isDesktop ? '50vw' : '100vw'}
                    src={item.image_link}
                    forPage="pdp"
                    trim="auto"
                  />
                </div>
              </div>
            </div>
          ))}
        </InfiniteScroll>
        {!loadingMore && loadMore && (
          <div className="flex flex-col items-center justify-center mt-6 mx-0 mb-[.3rem] max-[767px]:mb-4">
            <button
              onClick={loadMore}
              className="w-[200px] text-center leading-[50px] cursor-pointer uppercase text-[.8em] text-gray border border-solid [border-color:#dbdbdb] rounded-[.1875rem]"
              type="button"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return isDesktop ? (
    content
  ) : (
    <MobileModal
      closeIcon="arrow"
      isOpen
      onClose={toggleModal}
      variant="GridWithSidebar"
    >
      {content}
    </MobileModal>
  );
};

GridWithSidebarPhotoModal.propTypes = {
  toggleModal: PropTypes.bool,
  photosList: PropTypes.array,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.bool,
  photo: PropTypes.object,
};

export default withBrowserOnly(GridWithSidebarPhotoModal);
